import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from '../../i18n';
import { removeAll } from '../../hooks/hooks.localStorage';
import {
  LayoutDashboard, ShoppingCart, TrendingUp, Package,
  Megaphone, DollarSign, Users, UserCircle, LogOut,
  ChevronLeft, ChevronRight, Building2, X, PiggyBank
} from 'lucide-react';

export default function Sidebar({ mobileOpen, onClose, collapsed, onToggleCollapsed }) {
  const { t, locale, toggleLocale } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector(s => s.userReducer.user);
  const isInvestor = user?.role === 'investisseur';

  const handleLogout = () => {
    dispatch({ type: 'logout' });
    removeAll();
    navigate('/login');
  };

  const navItems = [
    { to: '/dashboard', icon: LayoutDashboard, label: t.nav.dashboard, always: true },
    { to: '/products', icon: Package, label: t.nav.products, always: true },
    { to: '/purchases', icon: ShoppingCart, label: t.nav.purchases, always: true },
    { to: '/sales', icon: TrendingUp, label: t.nav.sales, always: true },
    { to: '/investments', icon: DollarSign, label: t.nav.investments, always: true },
    { to: '/campaigns', icon: Megaphone, label: t.nav.campaigns, always: true },
    { to: '/companies', icon: Building2, label: t.nav.companies, always: true },
    { to: '/cagnotte', icon: PiggyBank, label: t.nav.cagnotte, always: true },
    { to: '/partners', icon: Users, label: t.nav.partners, investorOnly: true },
  ].filter(item => item.always || (item.investorOnly && isInvestor));

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="sidebar-overlay"
          onClick={onClose}
          style={{ display: 'block' }}
          aria-hidden="true"
        />
      )}

      <aside className={`sidebar ${collapsed ? 'collapsed' : ''} ${mobileOpen ? 'mobile-open' : ''}`}>
        {/* Logo */}
        <div className="sidebar-logo">
          <img src="/logo.png" alt="SavInvest" onError={(e) => { e.target.style.display = 'none'; }} />
          <span className="sidebar-logo-text">SavInvest</span>
          {mobileOpen && (
            <button
              className="sidebar-close"
              onClick={onClose}
              aria-label="Close menu"
            >
              <X size={18} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="sidebar-nav">
          <div className="sidebar-section-title">Menu</div>
          {navItems.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
              data-tooltip={item.label}
              onClick={() => mobileOpen && onClose()}
            >
              <item.icon size={18} strokeWidth={2} />
              <span className="nav-item-label">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        {/* Footer */}
        <div className="sidebar-footer">
          {/* Language switch (the navbar one is hidden on phones) */}
          <div className="lang-toggle sidebar-lang">
            {['fr', 'en'].map(l => (
              <button
                key={l}
                className={`lang-btn ${locale === l ? 'active' : ''}`}
                onClick={() => locale !== l && toggleLocale()}
              >
                {l.toUpperCase()}
              </button>
            ))}
          </div>
          <NavLink
            to="/profile"
            className={({ isActive }) => `nav-item ${isActive ? 'active' : ''}`}
            data-tooltip={t.nav.profile}
            onClick={() => mobileOpen && onClose()}
          >
            <UserCircle size={18} />
            <span className="nav-item-label">{t.nav.profile}</span>
          </NavLink>
          <button className="nav-item" onClick={handleLogout} data-tooltip={t.nav.logout}>
            <LogOut size={18} />
            <span className="nav-item-label">{t.nav.logout}</span>
          </button>

          {/* Collapse toggle (desktop only) */}
          <button
            className="sidebar-toggle"
            onClick={onToggleCollapsed}
            title={collapsed ? 'Expand' : 'Collapse'}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
          </button>
        </div>
      </aside>
    </>
  );
}